/**
 * AppointmentExam Types
 * Types for complementary exams linked to appointments
 */

export enum AppointmentExamStatus {
  PENDING = 'PENDING',
  IN_PROGRESS = 'IN_PROGRESS',
  COMPLETED = 'COMPLETED',
  CANCELLED = 'CANCELLED',
  NOT_APPLICABLE = 'NOT_APPLICABLE',
}

export enum AppointmentExamResult {
  NORMAL = 'NORMAL',
  ALTERED = 'ALTERED',
  INCONCLUSIVE = 'INCONCLUSIVE',
}

export enum ReferralStatus {
  PENDING_SEND = 'PENDING_SEND',
  SENT = 'SENT',
  ACCEPTED = 'ACCEPTED',
  REFUSED = 'REFUSED',
  RESCHEDULE_REQUESTED = 'RESCHEDULE_REQUESTED',
}

export type ConsultationMode = 'FULL' | 'EXAM_ONLY';

export interface OccupationalExamSummary {
  id: number;
  examName: string;
  esocialCode?: string | null;
  tussCode?: string | null;
  clinicalConsultation: boolean;
  examPreparation?: string | null;
  criteria?: string | null;
}

export interface AppointmentExam {
  id: number;
  appointmentId: number;
  occupationalExamId: number;
  status: AppointmentExamStatus;
  result: AppointmentExamResult | null;
  resultDetails: string | null;
  observations: string | null;
  filePath: string | null;
  originalFileName: string | null;
  fileSize: number | null;
  fileMimeType: string | null;
  completedAt: string | null;
  completedById: number | null;
  applicationLevel: string | null;
  periodicityMonths: number | null;
  referralId: number | null;
  referral?: { id: number; clinicId: number; status: string; clinic?: { id: number; tradeName?: string | null; legalName: string } };
  occupationalExam?: OccupationalExamSummary;
  completedBy?: { id: number; name: string };
  createdAt: string;
  updatedAt: string;
}

export interface SuggestedExam {
  examId: number;
  examName: string;
  esocialCode: string | null;
  tussCode: string | null;
  clinicalConsultation: boolean;
  isRequired: boolean;
  applicationLevel: 'company' | 'department' | 'occupation' | 'employee';
  periodicityMonths: number | null;
}

export interface UpdateExamResultData {
  status?: AppointmentExamStatus;
  result?: AppointmentExamResult | null;
  resultDetails?: string | null;
  observations?: string | null;
}

// Status labels
export const APPOINTMENT_EXAM_STATUS_LABELS: Record<AppointmentExamStatus, string> = {
  [AppointmentExamStatus.PENDING]: 'Pendente',
  [AppointmentExamStatus.IN_PROGRESS]: 'Em Andamento',
  [AppointmentExamStatus.COMPLETED]: 'Concluído',
  [AppointmentExamStatus.CANCELLED]: 'Cancelado',
  [AppointmentExamStatus.NOT_APPLICABLE]: 'Não Aplicável',
};

export const APPOINTMENT_EXAM_STATUS_COLORS: Record<AppointmentExamStatus, 'default' | 'primary' | 'success' | 'error' | 'warning' | 'info'> = {
  [AppointmentExamStatus.PENDING]: 'default',
  [AppointmentExamStatus.IN_PROGRESS]: 'info',
  [AppointmentExamStatus.COMPLETED]: 'success',
  [AppointmentExamStatus.CANCELLED]: 'error',
  [AppointmentExamStatus.NOT_APPLICABLE]: 'warning',
};

export const APPOINTMENT_EXAM_RESULT_LABELS: Record<AppointmentExamResult, string> = {
  [AppointmentExamResult.NORMAL]: 'Normal',
  [AppointmentExamResult.ALTERED]: 'Alterado',
  [AppointmentExamResult.INCONCLUSIVE]: 'Inconclusivo',
};

export const APPOINTMENT_EXAM_RESULT_COLORS: Record<AppointmentExamResult, 'success' | 'error' | 'warning'> = {
  [AppointmentExamResult.NORMAL]: 'success',
  [AppointmentExamResult.ALTERED]: 'error',
  [AppointmentExamResult.INCONCLUSIVE]: 'warning',
};

// Referral status labels
export const REFERRAL_STATUS_LABELS: Record<ReferralStatus, string> = {
  [ReferralStatus.PENDING_SEND]: 'Pendente de Envio',
  [ReferralStatus.SENT]: 'Enviado',
  [ReferralStatus.ACCEPTED]: 'Aceito',
  [ReferralStatus.REFUSED]: 'Recusado',
  [ReferralStatus.RESCHEDULE_REQUESTED]: 'Reagendamento Solicitado',
};

export const REFERRAL_STATUS_COLORS: Record<ReferralStatus, 'default' | 'primary' | 'success' | 'error' | 'warning' | 'info'> = {
  [ReferralStatus.PENDING_SEND]: 'default',
  [ReferralStatus.SENT]: 'info',
  [ReferralStatus.ACCEPTED]: 'success',
  [ReferralStatus.REFUSED]: 'error',
  [ReferralStatus.RESCHEDULE_REQUESTED]: 'warning',
};

// Application level labels and colors (shared across ExamSelector, Reception, Consultation)
export const APPLICATION_LEVEL_LABELS: Record<string, string> = {
  company: 'Empresa',
  department: 'Setor',
  occupation: 'Cargo',
  employee: 'Funcionario',
};

export const APPLICATION_LEVEL_COLORS: Record<string, 'default' | 'primary' | 'secondary' | 'info' | 'success'> = {
  company: 'primary',
  department: 'info',
  occupation: 'secondary',
  employee: 'success',
};
